import React, { useState, useCallback, useRef } from 'react'
import {
  Box,
  Popover,
  Typography,
  IconButton,
  Avatar,
  InputBase,
  Divider,
  ButtonBase,
  CircularProgress,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'

import StarIcon from '@hapana/shared/web/styles/assets/svgs/star-empty.svg'
import StarFilledIcon from '@hapana/shared/web/styles/assets/svgs/star-filled.svg'
import SearchIcon from '@hapana/shared/web/styles/assets/svgs/search.svg'
import AngleDownIcon from '@hapana/shared/web/styles/assets/svgs/angle-down-small.svg'
import LocationIcon from '@hapana/shared/web/styles/assets/svgs/location.svg'
import BrandHome from '@hapana/shared/web/styles/assets/svgs/brand-home.svg'

import useSiteStore from '../../stores/site.store'
import { useLocationSelector, type SiteRow } from '../../hooks/use-location-selector'
import { usePermissions } from '../../stores/permissions.store'

const styles = {
  locationItem: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: 1.5,
    px: 2,
    py: 1,
    borderRadius: 1,
    textAlign: 'left',
    transition: 'background-color 0.15s ease',
  },
  favoriteButton: {
    p: 0.5,
    flexShrink: 0,
  },
  icon: {
    width: 16,
    height: 16,
  },
  avatar: {
    width: 24,
    height: 24,
    fontSize: '0.7rem',
    fontWeight: 500,
    backgroundColor: 'gray.16',
    color: 'text.primary',
    flexShrink: 0,
  },
  siteName: {
    fontSize: 13,
    color: 'text.primary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  selectedDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: 'primary.main',
    flexShrink: 0,
  },
  triggerButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    borderRadius: 1,
    px: 1.25,
    py: 0.375,
    border: '1px solid',
    '&.Mui-disabled': {
      opacity: 0.5,
      borderColor: 'gray.16',
    },
    transition: 'all 0.15s ease',
  },
  triggerLabel: {
    fontSize: 11,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  chevron: {
    width: 10,
    height: 10,
    transition: 'transform 0.2s ease',
  },
  popoverPaper: {
    width: 320,
    maxHeight: 420,
    mt: 1,
    borderRadius: 2,
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  searchWrapper: {
    px: 2,
    pt: 2,
    pb: 1,
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.5,
    py: 0.75,
    border: '1px solid',
    borderColor: 'gray.16',
    borderRadius: 1,
    '&:focus-within': { borderColor: 'primary.main' },
    transition: 'border-color 0.15s ease',
  },
  searchIcon: {
    width: 16,
    height: 16,
    opacity: 0.5,
    flexShrink: 0,
  },
  searchInput: {
    fontSize: 13,
    flex: 1,
  },
  contentArea: {
    flex: 1,
    overflow: 'auto',
    pb: 1,
  },
  centeredMessage: {
    px: 2,
    py: 3,
    textAlign: 'center',
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    py: 3,
  },
  sectionLabel: {
    px: 2,
    py: 0.75,
    fontSize: 11,
    fontWeight: 600,
    color: 'text.disabled',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  divider: {
    my: 0.5,
  },
  brandButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    gap: 1.5,
    px: 2,
    py: 1,
    textAlign: 'left',
    transition: 'background-color 0.15s ease',
    '&:hover': { backgroundColor: 'action.hover' },
  },
  brandIcon: {
    width: 20,
    height: 20,
    color: 'primary.main',
    flexShrink: 0,
  },
  brandLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: 'primary.main',
  },
  // Pill variant styles — compact on desktop (sm+), full-width on mobile (xs)
  pillButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: { xs: 1, sm: 0.5 },
    width: { xs: '100%', sm: 'auto' },
    px: { xs: 2.5, sm: 1.5 },
    py: { xs: 1.25, sm: 0.5 },
    borderRadius: 99,
    bgcolor: 'primary.main',
    transition: 'opacity 0.15s ease',
    '&:hover': { opacity: 0.9 },
    '&:active': { opacity: 0.85 },
    '&.Mui-disabled': { opacity: 0.5 },
  },
  pillIcon: {
    width: { xs: 18, sm: 14 },
    height: { xs: 18, sm: 14 },
    color: 'common.white',
    flexShrink: 0,
  },
  pillLabel: {
    fontSize: { xs: 14, sm: 11 },
    fontWeight: 600,
    color: 'common.white',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pillChevron: {
    width: { xs: 14, sm: 10 },
    height: { xs: 14, sm: 10 },
    color: 'common.white',
    flexShrink: 0,
    transition: 'transform 0.2s ease',
  },
  scrollSentinel: {
    height: 1,
  },
  spinnerBox: {
    display: 'flex',
    justifyContent: 'center',
    py: 1,
  },
  footer: {
    borderTop: '1px solid',
    borderColor: 'gray.8',
    px: 2,
    py: 0.75,
    textAlign: 'center',
    flexShrink: 0,
  },
  footerText: {
    fontSize: 11,
    color: 'text.disabled',
  },
}

const LocationItem: React.FC<{
  site: SiteRow
  isFavorite: boolean
  isSelected: boolean
  isTogglingFavorite: boolean
  onToggleFavorite: (siteId: string) => void
  onSelect: (site: SiteRow) => void
}> = ({ site, isFavorite, isSelected, isTogglingFavorite, onToggleFavorite, onSelect }) => {
  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleFavorite(site.siteId)
  }

  return (
    <ButtonBase
      component="div"
      onClick={() => onSelect(site)}
      sx={{
        ...styles.locationItem,
        backgroundColor: isSelected ? 'action.selected' : 'transparent',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <IconButton
        size="small"
        onClick={handleFavoriteClick}
        disabled={isTogglingFavorite}
        sx={styles.favoriteButton}
      >
        {isFavorite ? (
          <Box component={StarFilledIcon} sx={styles.icon} />
        ) : (
          <Box component={StarIcon} sx={styles.icon} />
        )}
      </IconButton>

      <Avatar sx={styles.avatar}>{site.name?.trim()?.charAt(0)?.toUpperCase() || '?'}</Avatar>

      <Typography
        sx={{
          ...styles.siteName,
          fontWeight: isSelected ? 600 : 400,
        }}
      >
        {site.name}
      </Typography>

      {isSelected && <Box sx={styles.selectedDot} />}
    </ButtonBase>
  )
}

const LocationSwitcher: React.FC<{ variant?: 'default' | 'pill' }> = ({ variant = 'default' }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const { clearCurrentSiteWithBrandIntent } = useSiteStore()
  const hasSiteContext = Boolean(useSiteStore((s) => s.currentSite?.siteId))
  const { hasPermission } = usePermissions()
  const hasBrandAccess = hasPermission('read_controls') || hasPermission('write_controls')

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleGoToBrand = useCallback(() => {
    clearCurrentSiteWithBrandIntent()
    navigate('/')
    handleClose()
  }, [clearCurrentSiteWithBrandIntent, navigate, handleClose])

  const {
    favoriteLocations,
    nonFavoriteLocations,
    currentSite,
    isLoading,
    isTogglingFavorite,
    error,
    search,
    setSearch,
    handleToggleFavorite,
    handleSiteSelect,
    isEmpty,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    totalCount,
    loadedCount,
  } = useLocationSelector({ onSiteSelected: handleClose })

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
  }, [])

  // Infinite scroll — callback ref attaches IntersectionObserver when sentinel mounts
  const observerRef = useRef<IntersectionObserver>()
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect()
      if (!node) return

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
          }
        },
        { threshold: 0.1 }
      )
      observerRef.current.observe(node)
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  )

  const allSites = [...favoriteLocations, ...nonFavoriteLocations]

  const buttonLabel = isEmpty
    ? t('core:locationSwitch.noLocations.button', 'No locations')
    : hasSiteContext
      ? t('core:locationSwitch.changeLocation', 'Change location')
      : t('core:locationSwitch.selectLocation', 'Select location')

  const isPill = variant === 'pill'
  const pillLabel = currentSite?.name || buttonLabel

  return (
    <>
      {isPill ? (
        <ButtonBase onClick={handleOpen} disabled={isEmpty} sx={styles.pillButton}>
          <Box component={LocationIcon} sx={styles.pillIcon} />
          <Typography sx={styles.pillLabel} noWrap>
            {pillLabel}
          </Typography>
          <Box
            component={AngleDownIcon}
            sx={{
              ...styles.pillChevron,
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </ButtonBase>
      ) : (
        <ButtonBase
          onClick={handleOpen}
          disabled={isEmpty}
          sx={{
            ...styles.triggerButton,
            borderColor: open ? 'primary.main' : 'gray.16',
            backgroundColor: open ? 'primary.lightest' : 'transparent',
            '&:hover': { borderColor: 'gray.32' },
          }}
        >
          <Typography
            sx={{
              ...styles.triggerLabel,
              color: isEmpty ? 'text.disabled' : open ? 'primary.main' : 'text.secondary',
            }}
          >
            {buttonLabel}
          </Typography>
          {!isEmpty && (
            <Box
              component={AngleDownIcon}
              sx={{
                ...styles.chevron,
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          )}
        </ButtonBase>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: styles.popoverPaper } }}
      >
        {/* Search */}
        <Box sx={styles.searchWrapper}>
          <Box sx={styles.searchBox}>
            <Box component={SearchIcon} sx={styles.searchIcon} />
            <InputBase
              placeholder={t('core:locationSwitch.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={styles.searchInput}
              autoFocus
            />
          </Box>
        </Box>

        {/* Go to Brand Level — only show when inside a location */}
        {hasBrandAccess && hasSiteContext && (
          <>
            <ButtonBase onClick={handleGoToBrand} sx={styles.brandButton}>
              <Box component={BrandHome} sx={styles.brandIcon} />
              <Typography sx={styles.brandLabel}>
                {t('core:locationSwitch.goToBrandLevel', 'Go to brand level')}
              </Typography>
            </ButtonBase>
            <Divider sx={styles.divider} />
          </>
        )}

        {/* Content */}
        <Box sx={styles.contentArea}>
          {error ? (
            <Box sx={styles.centeredMessage}>
              <Typography color="error" sx={{ fontSize: 13 }}>
                {t('core:locationSwitch.error', 'Failed to load locations. Please try again.')}
              </Typography>
            </Box>
          ) : isLoading ? (
            <Box sx={styles.loadingBox}>
              <Loading />
            </Box>
          ) : allSites.length === 0 ? (
            <Box sx={styles.centeredMessage}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {search
                  ? t('core:locationSwitch.noResults', 'No locations found')
                  : t('core:locationSwitch.noLocations.title')}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Favorites */}
              {favoriteLocations.length > 0 && (
                <>
                  <Typography sx={styles.sectionLabel}>
                    {t('core:locationSwitch.favorites')}
                  </Typography>
                  {favoriteLocations.map((site) => (
                    <LocationItem
                      key={site.siteId}
                      site={site}
                      isFavorite
                      isSelected={currentSite?.siteId === site.siteId}
                      isTogglingFavorite={isTogglingFavorite}
                      onToggleFavorite={handleToggleFavorite}
                      onSelect={handleSiteSelect}
                    />
                  ))}
                  <Divider sx={styles.divider} />
                </>
              )}

              {/* All Locations */}
              {nonFavoriteLocations.length > 0 && (
                <>
                  <Typography sx={styles.sectionLabel}>
                    {t('core:locationSwitch.allLocations')}
                  </Typography>
                  {nonFavoriteLocations.map((site) => (
                    <LocationItem
                      key={site.siteId}
                      site={site}
                      isFavorite={false}
                      isSelected={currentSite?.siteId === site.siteId}
                      isTogglingFavorite={isTogglingFavorite}
                      onToggleFavorite={handleToggleFavorite}
                      onSelect={handleSiteSelect}
                    />
                  ))}
                </>
              )}

              {/* Infinite scroll sentinel */}
              <Box ref={sentinelRef} sx={styles.scrollSentinel} />
              {isFetchingNextPage && (
                <Box sx={styles.spinnerBox}>
                  <CircularProgress size={20} />
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Fixed footer — always visible */}
        {totalCount > 0 && !isLoading && (
          <Box sx={styles.footer}>
            <Typography sx={styles.footerText}>
              {t('core:locationSwitch.loadedCount', {
                loaded: loadedCount,
                total: totalCount,
                defaultValue: `${loadedCount} of ${totalCount} locations`,
              })}
            </Typography>
          </Box>
        )}
      </Popover>
    </>
  )
}

export default LocationSwitcher
